// Sprint 12.5 ACC.B3 (#417) — Delivery Mode badge.
//
// Per-project visual indicator of delivery_mode. Used today on the
// project dashboard header. Two states:
//   • 'governed'   — Track A's existing behaviour. --primary token
//                    (Graphite Violet #7C3AED) since governed mode IS
//                    the platform's primary path.
//   • 'accelerate' — Track B autonomous-transition behaviour. --signal
//                    token (Signal Green #00E5A0 per Graphite Violet
//                    design system). Brand-primitive for high-energy
//                    state indicators.
//
// Pattern matches the inline-styled CSS-variable approach used by
// ImplementationPromptCard's StatusBadge rather than PhaseBadge's
// Tailwind-class approach. CSS-variable styling lets the badge
// automatically pick up the brand colours without needing the design
// system's token names duplicated as Tailwind aliases.

import type { CSSProperties } from 'react';

interface DeliveryModeBadgeProps {
  mode: 'governed' | 'accelerate';
  /** Optional override style merged into the badge container. */
  style?: CSSProperties;
}

export default function DeliveryModeBadge({ mode, style }: DeliveryModeBadgeProps) {
  const isAccelerate = mode === 'accelerate';
  const color = isAccelerate ? 'var(--signal)' : 'var(--primary)';
  const bgWithAlpha = isAccelerate
    ? 'rgba(0, 229, 160, 0.12)'    // --signal at 12% alpha
    : 'rgba(124, 58, 237, 0.10)';  // --primary at 10% alpha
  const borderWithAlpha = isAccelerate
    ? 'rgba(0, 229, 160, 0.45)'
    : 'rgba(124, 58, 237, 0.35)';
  const label = isAccelerate ? 'Accelerate' : 'Governed';

  return (
    <span
      data-testid="delivery-mode-badge"
      data-mode={mode}
      style={{
        fontFamily:    'var(--font-body)',
        fontSize:      11,
        fontWeight:    600,
        padding:       '3px 8px',
        borderRadius:  12,
        background:    bgWithAlpha,
        border:        `1px solid ${borderWithAlpha}`,
        color,
        whiteSpace:    'nowrap',
        flexShrink:    0,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        ...style,
      }}
    >
      {label}
    </span>
  );
}
